import { AppError } from '@/services/api/errors'
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form'

/**
 * Maps backend validation `errors` onto react-hook-form fields.
 * Returns true when any field error was applied.
 */
export function applyApiFieldErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
): boolean {
  if (!(error instanceof AppError) || !error.details) return false

  let applied = false
  for (const [field, messages] of Object.entries(error.details)) {
    const message = messages?.[0]
    if (!message) continue
    setError(toFormPath<T>(field), { type: 'server', message })
    applied = true
  }
  return applied
}

/** Backend array paths (`items[0].quantity`) → react-hook-form paths. */
function toFormPath<T extends FieldValues>(field: string): Path<T> {
  return field.replace(/\[(\d+)\]/g, '.$1') as Path<T>
}

/**
 * Maps 409 conflicts onto form fields. Uses `errors` when present;
 * otherwise places the backend message on `fallbackFields` (e.g. name/code).
 */
export function applyConflictFieldErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fallbackFields: Path<T>[] = [],
): boolean {
  if (!(error instanceof AppError) || error.code !== 'CONFLICT') return false

  if (applyApiFieldErrors(error, setError)) return true

  const message = error.message?.trim()
  if (!message || fallbackFields.length === 0) return false

  for (const field of fallbackFields) {
    setError(field, { type: 'server', message })
  }
  return true
}

export function getApiErrorToastMessage(
  error: unknown,
  fallback = 'Something went wrong',
): string {
  if (error instanceof AppError) {
    if (error.code === 'FORBIDDEN') {
      return 'No permission'
    }
    // 503 = the server says exactly what is wrong (e.g. a deploy step still to run).
    if (error.status === 503 && error.message) {
      return error.message
    }
    if (error.status != null && error.status >= 500) {
      return 'Something went wrong. Please try again.'
    }
    if (error.code === 'VALIDATION' && error.details) {
      return error.message || 'Please fix the highlighted fields'
    }
    return error.message || fallback
  }
  if (error instanceof Error) return error.message
  return fallback
}
